import { anomalies, mails } from "../data/generated";
import type { AnomalyDefinition, GameState, MailTemplate } from "../types";
import { applyAnomaly, createNormalMail, isAnomalyCompatible } from "./anomalyEngine";
import { getAnomalyProbability } from "./progression";
import { randomAt, weightedPick } from "./rng";
import { applyStoryEvent, findPendingStoryEvent } from "./storyEngine";

function isMailCandidate(mail: MailTemplate, state: GameState): boolean {
  if (mail.tags.includes("story")) return false;
  if (!mail.allowedPhases.includes(state.phase)) return false;
  const recent = state.seenMailIds.slice(-2);
  if (recent.includes(mail.id)) return false;
  return true;
}

function anomalyCandidates(mail: MailTemplate, state: GameState): AnomalyDefinition[] {
  return anomalies.filter((anomaly) => {
    if (!anomaly.allowedPhases.includes(state.phase)) return false;
    if (!isAnomalyCompatible(mail, anomaly)) return false;
    const uses = state.anomalyUseCounts[anomaly.id] ?? 0;
    return uses < anomaly.maxUsesPerRun;
  });
}

export function generateNextMail(input: GameState): GameState {
  if (input.phase === "final") {
    const finalMail = mails.find((mail) => mail.id === "story_final");
    if (!finalMail) throw new Error("Final mail is missing.");
    return { ...input, currentMail: { ...createNormalMail(finalMail), isAnomaly: true, anomalyType: "retired_sender", debugExplanation: "退職者から送信された最終メール。" } };
  }

  const pendingEvent = findPendingStoryEvent(input);
  if (pendingEvent) {
    const withEvent = applyStoryEvent(input, pendingEvent);
    if (withEvent.currentMail) return withEvent;
  }

  let state = input;
  let candidates = mails.filter((mail) => isMailCandidate(mail, state));
  if (candidates.length === 0) {
    candidates = mails.filter((mail) => !mail.tags.includes("story") && mail.allowedPhases.includes(state.phase));
  }
  if (candidates.length === 0) {
    throw new Error(`No mail candidates for phase ${state.phase}`);
  }

  const selectedMail = weightedPick(state.seed, state.rngStep, candidates, (mail) => mail.weight);
  state = { ...state, rngStep: selectedMail.step };

  const probabilityRoll = randomAt(state.seed, state.rngStep);
  state = { ...state, rngStep: probabilityRoll.step };
  const shouldApplyAnomaly = probabilityRoll.value < getAnomalyProbability(state.phase);
  const compatible = anomalyCandidates(selectedMail.value, state);

  if (!shouldApplyAnomaly || compatible.length === 0) {
    return { ...state, currentMail: createNormalMail(selectedMail.value) };
  }

  const selectedAnomaly = weightedPick(
    state.seed,
    state.rngStep,
    compatible,
    (anomaly) => Math.max(1, 6 - anomaly.difficulty)
  );
  state = { ...state, rngStep: selectedAnomaly.step };
  return { ...state, currentMail: applyAnomaly(selectedMail.value, selectedAnomaly.value) };
}
